import type { TavernProgressionRow } from '@/api/hooks'

interface Props {
  progression: TavernProgressionRow[]
  /** Highlight the player's current tavern level row. */
  currentLevel?: number
}

const FAMILY_LABELS: Record<string, string> = {
  common: 'Communes',
  pvp: 'PvP',
  narrative: 'Narratives',
  rare: 'Rares & chasses au trésor',
}

/** Neutre (×1) sous ce niveau : pas de bonus à afficher, juste un tiret pour ne pas polluer. */
const QUEST_REP_MULTIPLIER_NEUTRAL = 1

/**
 * Level-by-level unlock table for the Tavern.
 * Shows what's NEW at each level (diff vs the previous row's unlockedFamilies),
 * the quest board capacity (offers + active slots), the offer cadence, and the
 * réputation vivante bonus on quest rewards (neutral until level 5, then +25 %/niveau).
 */
export function TavernProgressionTable({ progression, currentLevel }: Props) {
  return (
    <div className="mt-2 max-h-72 overflow-auto rounded border border-ocean-500/20 bg-ocean-900/40">
      <table className="w-full min-w-[400px] text-xs text-ocean-100">
        <thead className="sticky top-0 bg-ocean-900 text-ocean-300">
          <tr>
            <th className="px-2 py-1.5 text-left font-semibold">Niv.</th>
            <th className="px-2 py-1.5 text-right font-semibold">Offres</th>
            <th className="px-2 py-1.5 text-right font-semibold">Actives</th>
            <th className="px-2 py-1.5 text-right font-semibold">Cadence</th>
            <th className="px-2 py-1.5 text-right font-semibold">Bonus réput.</th>
            <th className="px-2 py-1.5 text-left font-semibold">Débloque</th>
          </tr>
        </thead>
        <tbody>
          {progression.map((row, idx) => {
            const prev = idx > 0 ? progression[idx - 1] : null
            const isCurrent = currentLevel !== undefined && row.level === currentLevel
            const isNext = currentLevel !== undefined && row.level === currentLevel + 1

            // Compute families unlocked at this level vs the previous row
            const prevFamilies = prev?.unlockedFamilies ?? []
            const newFamilies = row.unlockedFamilies.filter((f) => !prevFamilies.includes(f))
            const unlockLabel =
              newFamilies.length > 0
                ? newFamilies.map((f) => FAMILY_LABELS[f] ?? f).join(', ')
                : '—'

            // Cadence change cue
            const cadenceChanged = prev !== null && row.offerCooldownHours !== prev.offerCooldownHours
            const cadenceLabel = `${row.offerCooldownHours}h`

            // Réputation vivante : neutre (×1) en dessous du palier — un tiret plutôt que "+0 %" pour ne pas polluer.
            const questRepBonusLabel =
              row.questRepMultiplier > QUEST_REP_MULTIPLIER_NEUTRAL
                ? `+${Math.round((row.questRepMultiplier - 1) * 100)} %`
                : '—'

            return (
              <tr
                key={row.level}
                className={
                  isCurrent
                    ? 'bg-sand-500/20 text-sand-100'
                    : isNext
                      ? 'bg-ocean-500/10'
                      : 'odd:bg-ocean-700/20'
                }
              >
                <td className="px-2 py-1 whitespace-nowrap">
                  {row.level}
                  {isCurrent && <span className="ml-1 text-[10px] text-sand-300">actuel</span>}
                  {isNext && <span className="ml-1 text-[10px] text-ocean-300">suivant</span>}
                </td>
                <td className="px-2 py-1 text-right tabular-nums">{row.offersCap}</td>
                <td className="px-2 py-1 text-right tabular-nums">{row.activeCap}</td>
                <td className="px-2 py-1 text-right whitespace-nowrap tabular-nums">
                  {cadenceLabel}
                  {cadenceChanged && (
                    <span className="ml-1 text-[10px] text-green-300">↓</span>
                  )}
                </td>
                <td className="px-2 py-1 text-right whitespace-nowrap tabular-nums">{questRepBonusLabel}</td>
                <td className="px-2 py-1 text-ocean-200">{unlockLabel}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
